/**
 * Normalisation du paramètre `propFirm` qui transite par l'URL du callback OAuth.
 *
 * Le callback redirige vers `/trading-accounts?tradovateImport=<id>&propFirm=<id>`.
 * Quand l'utilisateur n'a choisi aucune prop firm, l'id est `null` côté serveur,
 * mais `URLSearchParams.get("propFirm")` rend `""` (chaîne vide) et non `null` :
 * le param est présent, simplement vide. Cette `""` traversait toute la chaîne
 * jusqu'à `Account.create({ propFirmTemplateId: "" })`, où Postgres cherchait un
 * PropFirmTemplate d'id `""` → violation de `Account_propFirmTemplateId_fkey`,
 * rollback de la transaction, import entièrement perdu.
 *
 * `propFirmTemplateId` est nullable en base : l'absence de prop firm est un cas
 * légitime. C'est `""` qui n'a aucun sens — jamais un id, jamais une absence
 * explicite. On le ramène donc à `null`, la seule représentation valide.
 *
 * Module pur (aucun import `wasp/server`, qui valide l'env à l'import et ne peut
 * pas être chargé sous vitest), à l'image de `tradovateAssignPropFirm.ts`.
 */

/**
 * Ramène à `null` toute valeur qui ne peut pas être un id de template :
 * `""`, une chaîne d'espaces, `undefined` ou `null`. Toute autre valeur est
 * rendue trimée — l'existence et l'ownership restent vérifiés côté serveur.
 */
export function normalizePropFirmTemplateId(
  raw: string | null | undefined,
): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Construit la query string de redirection du callback OAuth.
 *
 * `propFirm` n'est ajouté que s'il porte un id réel : un param vide est pire que
 * pas de param, puisqu'il se lit `""` et non `null` côté client. Les valeurs sont
 * encodées — un id se retrouve dans une URL, et un `&` ou `#` non échappé
 * casserait le parsing des params suivants.
 */
export function buildImportRedirectPath(
  connectionId: string,
  propFirmTemplateId: string | null | undefined,
): string {
  const params = new URLSearchParams({ tradovateImport: connectionId });
  const normalized = normalizePropFirmTemplateId(propFirmTemplateId);
  if (normalized) params.set("propFirm", normalized);
  return `/trading-accounts?${params.toString()}`;
}
